/**
 * src/pages/CriticalThinking/CriticalThinking.tsx
 * Critical Thinking Workshop - Practical analytical reasoning, argument anatomy,
 * claim dissection, and belief calibration.
 *
 * Page id: "critical", character: "critical".
 */

import React from "react";
import { KnowledgePageShell } from "@/components/knowledge/KnowledgePageShell";
import { PageHero } from "@/components/knowledge/PageHero";
import { SkillSetSection } from "./sections/SkillSetSection";
import { ClaimAnalyzerSection } from "./sections/ClaimAnalyzerSection";
import { ArgumentReconstructorSection } from "./sections/ArgumentReconstructorSection";
import { BeliefRevisionSection } from "./sections/BeliefRevisionSection";
import { CommonMistakesSection } from "./sections/CommonMistakesSection";
import { Link } from "@/components/common/Link";
import { ShieldCheck, Search, Layers, Compass, HelpCircle } from "lucide-react";
import "./critical.css";
export default function CriticalThinkingPage() {
  return <KnowledgePageShell pageId="critical" character="critical" accent="cyan" className="critical-page">
      {/* 1. Page Hero Banner */}
      <PageHero eyebrow="Architecture of Knowledge • Area 04" title="Critical Thinking Workshop" subtitle="The practical craft of reasoned evaluation. Dissect real-world claims, reconstruct everyday arguments in standard form, uncover hidden premises, steelman opposing perspectives, and master the discipline of belief calibration under uncertainty.">
        {/* Quick Nav Telemetry Deck */}
        <div className="ql-glass p-5 rounded-xl border border-primary/40 space-y-3">
          <span className="font-mono text-[11px] text-primary uppercase font-bold tracking-wider flex items-center gap-1.5">
            <ShieldCheck className="size-4" />
            <span>Interactive Instruments</span>
          </span>
          <div className="space-y-2">
            <Link to="#claim-analyzer" className="p-2.5 rounded border border-border/50 bg-card/40 hover:bg-card hover:border-primary/50 transition-colors flex items-center justify-between gap-2 group min-h-[44px]">
              <span className="font-serif text-xs font-semibold text-foreground group-hover:text-primary transition-colors flex items-center gap-2 min-w-0">
                <Search className="size-3.5 text-primary shrink-0" />
                <span className="break-words">Claim Analyzer (8-Step Framework)</span>
              </span>
              <span className="font-mono text-[10px] text-muted-foreground shrink-0">6 Specimens</span>
            </Link>

            <Link to="#argument-reconstructor" className="p-2.5 rounded border border-border/50 bg-card/40 hover:bg-card hover:border-primary/50 transition-colors flex items-center justify-between gap-2 group min-h-[44px]">
              <span className="font-serif text-xs font-semibold text-foreground group-hover:text-primary transition-colors flex items-center gap-2 min-w-0">
                <Layers className="size-3.5 text-primary shrink-0" />
                <span className="break-words">Argument Reconstructor & Steelman</span>
              </span>
              <span className="font-mono text-[10px] text-muted-foreground shrink-0">6 Disputes</span>
            </Link>

            <Link to="#belief-revision" className="p-2.5 rounded border border-border/50 bg-card/40 hover:bg-card hover:border-primary/50 transition-colors flex items-center justify-between gap-2 group min-h-[44px]">
              <span className="font-serif text-xs font-semibold text-foreground group-hover:text-primary transition-colors flex items-center gap-2 min-w-0">
                <Compass className="size-3.5 text-primary shrink-0" />
                <span className="break-words">Belief Calibration Simulator</span>
              </span>
              <span className="font-mono text-[10px] text-muted-foreground shrink-0">4 Stages</span>
            </Link>
          </div>
        </div>
      </PageHero>

      {/* Jump Bar for smooth internal navigation */}
      <div className="w-full max-w-[76rem] mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="p-2.5 rounded-lg border border-border/60 bg-card/40 flex items-center gap-2 overflow-x-auto text-xs font-mono">
          <span className="text-muted-foreground shrink-0 px-2 uppercase text-[11px] font-bold">
            Sections:
          </span>
          <Link to="#skill-set" className="px-3 py-1.5 rounded hover:bg-card/80 hover:text-primary text-foreground shrink-0 transition-colors">
            1. The Skill Set
          </Link>
          <Link to="#claim-analyzer" className="px-3 py-1.5 rounded hover:bg-card/80 hover:text-primary text-foreground shrink-0 transition-colors">
            2. Claim Analyzer
          </Link>
          <Link to="#argument-reconstructor" className="px-3 py-1.5 rounded hover:bg-card/80 hover:text-primary text-foreground shrink-0 transition-colors">
            3. Argument Reconstructor
          </Link>
          <Link to="#belief-revision" className="px-3 py-1.5 rounded hover:bg-card/80 hover:text-primary text-foreground shrink-0 transition-colors">
            4. Belief Revision
          </Link>
          <Link to="#common-mistakes" className="px-3 py-1.5 rounded hover:bg-card/80 hover:text-primary text-foreground shrink-0 transition-colors">
            5. Pitfalls & Challenge
          </Link>
        </div>
      </div>

      {/* 2. The Core Skill Set */}
      <SkillSetSection />

      {/* 3. CLAIM ANALYZER (id="claim-analyzer") */}
      <ClaimAnalyzerSection />

      {/* 4. ARGUMENT RECONSTRUCTOR (id="argument-reconstructor") */}
      <ArgumentReconstructorSection />

      {/* 5. BELIEF REVISION EXERCISE (id="belief-revision") & 6. What Would Change My Mind */}
      <BeliefRevisionSection />

      {/* 7. Common Mistakes, Final Challenge, & Related Links */}
      <CommonMistakesSection />
    </KnowledgePageShell>;
}